import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export enum VatIdError {
  /** Value does not match a supported VAT number regex. */
  FORMAT = 'vatIdFormat',
}

/**
 * VAT number format regexes for countries Stripe accepts as `eu_vat`, `gb_vat`, and `ch_vat`.
 */
const VAT_ID_PATTERNS: readonly RegExp[] = [
  /^ATU\d{8}$/,
  /^BE[01]\d{9}$/,
  /^BG\d{9,10}$/,
  /^CHE\d{9}(MWST|TVA|IVA)?$/,
  /^CY\d{8}[A-Z]$/,
  /^CZ\d{8,10}$/,
  /^DE\d{9}$/,
  /^DK\d{8}$/,
  /^EE\d{9}$/,
  /^EL\d{9}$/,
  /^ES[A-Z0-9]\d{7}[A-Z0-9]$/,
  /^FI\d{8}$/,
  /^FR[A-Z0-9]{2}\d{9}$/,
  /^GB(\d{9}|\d{12}|GD\d{3}|HA\d{3})$/,
  /^HR\d{11}$/,
  /^HU\d{8}$/,
  /^IE(\d{7}[A-W]|[7-9][A-Z*+]\d{5}[A-W]|\d{7}[A-W][AH])$/,
  /^IT\d{11}$/,
  /^LT(\d{9}|\d{12})$/,
  /^LU\d{8}$/,
  /^LV\d{11}$/,
  /^MT\d{8}$/,
  /^NL\d{9}B\d{2}$/,
  /^PL\d{10}$/,
  /^PT\d{9}$/,
  /^RO\d{2,10}$/,
  /^SE\d{12}$/,
  /^SI\d{8}$/,
  /^SK\d{10}$/,
];

/**
 * Strips separators and uppercases a VAT number so it can be matched and sent to Stripe.
 * @param {string} value Raw user input
 * @returns {string} Normalized VAT number
 */
export function normalizeVatId(value: string): string {
  return (value ?? '').replace(/[\s.\-/]/g, '').toUpperCase();
}

/**
 * Checks whether a VAT number matches a supported country format.
 * @param {string} value Raw user input
 * @returns {boolean} True when the normalized value matches a VAT regex
 */
export function isValidVatIdFormat(value: string): boolean {
  const normalized = normalizeVatId(value);
  if (!normalized) {
    return false;
  }
  return VAT_ID_PATTERNS.some((pattern) => pattern.test(normalized));
}

/**
 * Creates a validator for a VAT number field. Empty values are ignored so `Validators.required` can own that case.
 * @returns {ValidatorFn} Validator producing {@link VatIdError.FORMAT} when the regex does not match
 */
export function vatIdValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = (control.value ?? '') as string;
    if (!value.trim()) {
      return null;
    }
    return isValidVatIdFormat(value) ? null : { [VatIdError.FORMAT]: true };
  };
}
